/**
 * /frontend/src/features/analytics/pages/PeakAnalyticsPage.js
 */

import React, { useEffect, useMemo } from 'react';
import usePeakAnalytics from '../hooks/usePeakAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import FootfallHeatmap from '../components/peak/FootfallHeatmap';
import PeakHoursChart from '../components/peak/PeakHoursChart';
import DayComparisonChart from '../components/peak/DayComparisonChart';
import './PeakAnalyticsPage.css';

/**
 * Peak and heatmap analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const PeakAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const hook = usePeakAnalytics({ request, storeToken, storeUuid, startDate, endDate, weeks: 4, date: endDate, refreshSignal });
  const heatmap = hook.data?.heatmap || null;
  const topHours = hook.data?.topHours || null;
  const dayComparison = hook.data?.dayComparison || null;
  const syncing = hook.isLoading || hook.isRefreshing;

  const isEmpty = useMemo(() => !heatmap && !topHours && !dayComparison, [dayComparison, heatmap, topHours]);

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No peak analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <FootfallHeatmap heatmap={heatmap} />
      <div className="analytics-page-grid-2">
        <PeakHoursChart topHours={topHours} />
        <DayComparisonChart comparison={dayComparison} />
      </div>
    </div>
  );
};

export default PeakAnalyticsPage;
